/**
 * Onboarding Context Provider
 * Provides onboarding state and tour management throughout the app
 */

import { useState, useCallback, useMemo, useEffect, type ReactNode } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useOnboardingStorage } from '@/hooks/useOnboardingStorage';
import { OnboardingContext } from '@/contexts/onboarding-context';
import {
  type TourId,
  type ChecklistItemId,
  type OnboardingContextValue,
  CHECKLIST_ITEMS,
} from '@/types/onboarding';
import { tours } from '@/components/onboarding/tours';

interface OnboardingProviderProps {
  children: ReactNode;
}

/** Valid tour IDs derived from tours registry */
const VALID_TOUR_IDS = Object.keys(tours) as TourId[];

export function OnboardingProvider({ children }: OnboardingProviderProps) {
  const { state, updateState, resetState } = useOnboardingStorage();
  const [activeTourId, setActiveTourId] = useState<TourId | null>(null);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // Determine if onboarding UI should be visible
  const isVisible = useMemo(() => {
    return !state.dismissed && !state.welcomeShown;
  }, [state.dismissed, state.welcomeShown]);

  const isTourActive = activeTourId !== null;

  const startTour = useCallback((tourId: TourId) => {
    setActiveTourId(tourId);
  }, []);

  // Handle ?tour= URL parameter for shareable tour links
  useEffect(() => {
    const tourParam = searchParams.get('tour');

    if (tourParam && VALID_TOUR_IDS.includes(tourParam as TourId)) {
      // Start the tour (regardless of completion status)
      startTour(tourParam as TourId);

      // Remove parameter from URL to prevent re-triggering on refresh
      const newParams = new URLSearchParams(searchParams);
      newParams.delete('tour');
      navigate({ search: newParams.toString() }, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Run once on mount
  }, []);

  const completeTour = useCallback(
    (tourId: TourId) => {
      setActiveTourId(null);

      // Add to completed tours if not already there
      if (!state.completedTours.includes(tourId)) {
        updateState({
          completedTours: [...state.completedTours, tourId],
        });
      }
    },
    [state.completedTours, updateState]
  );

  const completeChecklistItem = useCallback(
    (itemId: ChecklistItemId) => {
      updateState({
        checklistProgress: {
          ...state.checklistProgress,
          [itemId]: true,
        },
      });
    },
    [state.checklistProgress, updateState]
  );

  const markWelcomeShown = useCallback(() => {
    updateState({ welcomeShown: true });
  }, [updateState]);

  const dismissOnboarding = useCallback(() => {
    updateState({ dismissed: true });
    setActiveTourId(null);
  }, [updateState]);

  const resetOnboarding = useCallback(() => {
    resetState();
    setActiveTourId(null);
  }, [resetState]);

  const getCompletionPercentage = useCallback(() => {
    const totalItems = CHECKLIST_ITEMS.length;
    const completedItems = Object.values(state.checklistProgress).filter(Boolean).length;
    return Math.round((completedItems / totalItems) * 100);
  }, [state.checklistProgress]);

  const value: OnboardingContextValue = useMemo(
    () => ({
      state,
      isVisible,
      isTourActive,
      activeTourId,
      startTour,
      completeTour,
      completeChecklistItem,
      markWelcomeShown,
      dismissOnboarding,
      resetOnboarding,
      getCompletionPercentage,
    }),
    [
      state,
      isVisible,
      isTourActive,
      activeTourId,
      startTour,
      completeTour,
      completeChecklistItem,
      markWelcomeShown,
      dismissOnboarding,
      resetOnboarding,
      getCompletionPercentage,
    ]
  );

  return <OnboardingContext.Provider value={value}>{children}</OnboardingContext.Provider>;
}
